import { cn } from '../cn.js';
import { Button } from './Button.js';

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Required, not optional — this is a `role="group"` toggle with no visible group label of its
   *  own, so screen-reader users need it to know what the pills switch. */
  ariaLabel: string;
  size?: 'sm' | 'md';
  /** Renders on the hero gradient rather than a plain surface — same convention as
   *  `Avatar.onHero`/`Badge.onHero`: a translucent-white track, the active pill a solid white chip
   *  (readable against the fixed hero gradient regardless of theme), inactive pills dimmed white. */
  onHero?: boolean;
}

/**
 * A row of mutually-exclusive pill buttons that set one enum value — the "My View / Team View"
 * toggle `PerformanceInsights.tsx` hand-rolled, generalized so the Goals status filter and the
 * Review Cycle Roster's status chips can use the same component instead of their own copies of
 * this exact bordered-pill-row markup.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'sm',
  onHero = false,
}: SegmentedControlProps<T>) {
  return (
    <div
      className={cn(
        // w-fit + nowrap pills: at phone width the row scrolls sideways instead of breaking
        // "Live (3)" over two lines inside a pill.
        'flex w-fit max-w-full items-center gap-0.5 rounded-md max-sm:overflow-x-auto border border-border bg-surface p-0.5',
        onHero && 'border-white/25 bg-white/10',
      )}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <Button
            key={option.value}
            type="button"
            variant={active ? 'primary' : 'ghost'}
            size={size}
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'shrink-0 whitespace-nowrap',
              onHero &&
                (active
                  ? 'bg-white text-accent hover:bg-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'),
            )}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
